import { silentKinds } from '$lib/api-error-handling';
import {
	ResourceType,
	type BillingInvoice,
	type BillingPayer,
	type BillingPaymentMethod,
} from '$lib/api/types.gen';
import { loadAuthzActions } from '$lib/stores/authz';

import {
	CONTACT_SUPPORT_TO_SET_UP,
	sharedBillingErrorMessage,
	type SetUpOffer,
} from './billing-messages';
import { formatMicroUsd } from './purchase';
import { formatUsageAmount } from './usage';

/** Query parameter that opens the add-payment-method dialog on the billing page (`/billing?add`). */
export const ADD_PAYMENT_METHOD_PARAM = 'add';

/**
 * Stripe returns to the page a purchase or card setup started on with the attempt in this query
 * parameter, appended by the backend.
 */
export const RETURNED_ATTEMPT_PARAM = 'attempt-id';

/** Whose billing: an organization's, or a user's own. */
export type BillingScope = { type: 'organization' | 'user'; id: string };

/** The scope of the payer the API names (`org` there is `organization` here). */
export function scopeOfPayer(payer: BillingPayer): BillingScope {
	return { type: payer.type === 'org' ? 'organization' : 'user', id: payer.id };
}

export function billingPagePath(scope: BillingScope): string {
	return scope.type === 'organization'
		? `/home/organization/${scope.id}/billing`
		: '/home/user/billing';
}

/** The usage page, which also shows the return of a purchase that left the page. */
export function usagePagePath(scope: BillingScope): string {
	return scope.type === 'organization'
		? `/home/organization/${scope.id}/usage`
		: '/home/user/usage';
}

/**
 * Who can get a payer's billing set up. `setupAvailable` is the API's flag (self-serve setup is on),
 * from the billing summary or from the refusal that names the payer; `undefined` when nothing says
 * (not readable, or the viewer can't see the payer's billing), which promises nothing. `canSetUp`
 * is the user themself, or an organization's billing managers.
 */
export function setUpOffer({
	setupAvailable,
	canSetUp,
}: {
	setupAvailable: boolean | undefined;
	canSetUp: boolean;
}): SetUpOffer {
	if (setupAvailable !== true) return 'contact-support';
	return canSetUp ? 'set-up' : 'ask-owner';
}

/** Whether the payer is a user other than the viewer, whose billing only they can set up. */
export function isAnotherUser(
	payer: BillingPayer | undefined,
	viewerId: string | undefined,
): boolean {
	return payer?.type === 'user' && payer.id !== viewerId;
}

/**
 * Where the payer's billing is set up: its Billing page. A user's is always the signed-in user's
 * own, so another user's is nowhere the viewer can go (`undefined`), not their own by mistake.
 */
export function setUpPageOf(payer: BillingPayer, viewerId: string | undefined): string | undefined {
	return isAnotherUser(payer, viewerId) ? undefined : billingPagePath(scopeOfPayer(payer));
}

/** What the viewer may do with an organization's billing. */
export async function loadOrganizationBillingRights(
	orgId: string,
): Promise<{ canManage: boolean; canView: boolean }> {
	const [manage, view] = await loadAuthzActions({
		resource: { type: ResourceType.ORG, id: orgId },
		checks: ['org:manage_billing', 'org:view_billing'],
	});
	// Those who may manage may also view, whichever way the roles are cut.
	return { canManage: manage, canView: manage || view };
}

type Card = Pick<BillingPaymentMethod, 'brand' | 'last4' | 'exp_month' | 'exp_year'>;

const CARD_BRANDS: Record<string, string> = {
	amex: 'American Express',
	cartes_bancaires: 'Cartes Bancaires',
	diners: 'Diners Club',
	discover: 'Discover',
	eftpos_au: 'EFTPOS',
	jcb: 'JCB',
	mastercard: 'Mastercard',
	unionpay: 'UnionPay',
	visa: 'Visa',
};

/** A card brand for display. The API says to tolerate unseen brands, so fall back to its words. */
export function cardBrandLabel(brand: string): string {
	const known = CARD_BRANDS[brand.toLowerCase()];
	if (known) return known;
	if (brand === '' || brand.toLowerCase() === 'unknown') return 'Card';
	const words = brand.replace(/_/g, ' ');
	return words.charAt(0).toUpperCase() + words.slice(1);
}

/** E.g. "Visa •••• 4242". */
export function describeCard(card: Card): string {
	return `${cardBrandLabel(card.brand)} •••• ${card.last4}`;
}

/** E.g. "03/2028". */
export function cardExpiry(card: Pick<Card, 'exp_month' | 'exp_year'>): string {
	return `${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`;
}

/** A card is good through the end of its expiry month. */
export function isCardExpired(card: Pick<Card, 'exp_month' | 'exp_year'>, now = new Date()) {
	const year = now.getUTCFullYear();
	return card.exp_year < year || (card.exp_year === year && card.exp_month < now.getUTCMonth() + 1);
}

/** Why a card can't be removed, as shown next to it; `''` when it can. */
export function removalBlockedMessage(
	// Null when removable; the generated type drops the `nullable` of an enum.
	reason: BillingPaymentMethod['removal_blocked_reason'] | null,
) {
	switch (reason) {
		case 'default_for_collection':
			return 'This is the default payment method. Make another one the default to remove it.';
		case 'only_payment_method':
			return 'This is the only payment method on file, so it can’t be removed.';
		default:
			return '';
	}
}

/** User-facing text for a refused billing call, by the API's error `kind`. */
/**
 * Billing refusals that are a normal outcome (deliberate configuration, a payer not set up, a stale
 * attempt or card, an existing plan), each worded inline by `sharedBillingErrorMessage` and the
 * billing, subscription and purchase messages. A provider outage is not: it toasts.
 */
export const billingRefusalErrors = silentKinds(
	'BILLING_DISABLED',
	'BILLING_NOT_PROVISIONED',
	'BILLING_SETUP_DISABLED',
	'SETUP_ATTEMPT_NOT_OPEN',
	'PAYMENT_METHOD_NOT_REMOVABLE',
	'SUBSCRIPTIONS_DISABLED',
	'SUBSCRIPTION_EXISTS',
	'PURCHASES_DISABLED',
	'PURCHASE_ATTEMPT_NOT_OPEN',
);

export function billingErrorMessage(kind: string | undefined, fallback: string): string {
	const shared = sharedBillingErrorMessage(kind);
	if (shared) return shared;
	switch (kind) {
		case 'BILLING_SETUP_DISABLED':
			return `Setting billing up yourself isn’t available. ${CONTACT_SUPPORT_TO_SET_UP}`;
		case 'SETUP_ATTEMPT_NOT_OPEN':
			return 'That setup is no longer open. Try again.';
		case 'PAYMENT_METHOD_NOT_REMOVABLE':
			return 'That payment method can no longer be removed.';
		default:
			return fallback || 'Something went wrong with billing.';
	}
}

export function invoiceStatusLabel(status: BillingInvoice['status']): string {
	switch (status) {
		case 'paid':
			return 'Paid';
		case 'open':
			return 'Unpaid';
		case 'draft':
			return 'Draft';
		case 'void':
			return 'Void';
		case 'uncollectible':
			return 'Uncollectible';
		default:
			return status;
	}
}

export function invoiceStatusVariant(
	status: BillingInvoice['status'],
): 'secondary' | 'outline' | 'destructive' {
	switch (status) {
		case 'paid':
			return 'secondary';
		case 'uncollectible':
			return 'destructive';
		default:
			return 'outline';
	}
}

/** What the invoice is for in dollars: what was collected once paid, the amount due before. */
export function formatInvoiceAmount(
	invoice: Pick<BillingInvoice, 'status' | 'amount_due_micro_usd' | 'amount_paid_micro_usd'>,
): string {
	const micro =
		invoice.status === 'paid' ? invoice.amount_paid_micro_usd : invoice.amount_due_micro_usd;
	return formatMicroUsd(BigInt(micro));
}

/** What an invoice bought, e.g. "1,337 Dream Bucks"; `undefined` for an invoice that isn't a purchase. */
export function formatInvoicePurchase(invoice: Pick<BillingInvoice, 'dream_bucks'>) {
	return invoice.dream_bucks === null
		? undefined
		: `${formatUsageAmount(invoice.dream_bucks)} Dream Bucks`;
}

const invoiceDate = new Intl.DateTimeFormat('en-US', {
	timeZone: 'UTC',
	month: 'short',
	day: 'numeric',
	year: 'numeric',
});

export function formatInvoiceDate(seconds: number): string {
	return invoiceDate.format(new Date(seconds * 1000));
}

/**
 * Refetch while the server reports a cached group as refreshing (§2.2 of the contract: about
 * every 2 s, for about 5 attempts), so a stale value gives way to the fresh one. Stops as soon as
 * nothing is refreshing. A failed refetch keeps the value on screen and tries again.
 * Returns a function that stops the watch.
 */
export function refetchWhileRefreshing<T>(
	initial: T,
	load: () => Promise<T>,
	isRefreshing: (value: T) => boolean,
	onValue: (value: T) => void,
	{ intervalMs = 2_000, maxRefetches = 5 }: { intervalMs?: number; maxRefetches?: number } = {},
) {
	let stopped = false;
	let timer: ReturnType<typeof setTimeout> | undefined;
	let refetches = 0;

	function schedule(value: T) {
		if (!isRefreshing(value) || refetches >= maxRefetches) return;
		timer = setTimeout(async () => {
			refetches += 1;
			let next = value;
			try {
				next = await load();
			} catch {
				// Keep what is on screen and try again.
			}
			if (stopped) return;
			if (next !== value) onValue(next);
			schedule(next);
		}, intervalMs);
	}

	schedule(initial);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

/** A value fetched at `asOfSeconds` is old enough to be worth labelling (default: 10 minutes). */
export function isStale(asOfSeconds: number | null, now = Date.now(), maxAgeMs = 10 * 60_000) {
	return asOfSeconds !== null && now - asOfSeconds * 1000 > maxAgeMs;
}
